import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  abandonSession,
  completeSession,
  getSession,
  issuePutUrls,
  openSession,
  presentPaths,
  reportProgress,
  type DataObject,
  type UploadSession,
} from '@/api/objects';
import { ApiError } from '@/api/client';
import { crc32cOfFile } from './crc32c';
import { uploadFile } from './resumable';
import { compareRoots, declaration, sameRoots, stagedFiles, summarize, totals, type RootDiff, type RootSummary, type Staged, type TreeFile } from './tree';
import { uploadTree } from './treeUpload';

export type UploadPhase = 'idle' | 'hashing' | 'opening' | 'uploading' | 'verifying' | 'done' | 'failed';

export interface UploadState {
  phase: UploadPhase;
  progress: { done: number; total: number };
  error?: string;
  /** For `different_tree` when the original roots are known: which of them differ. */
  mismatch?: RootDiff;
  result?: DataObject;
}

/** What survives a closed tab: enough to re-pick the same file or folders and continue (D8). */
export interface StoredFileUpload {
  shape?: 'file'; // absent on records written before tree uploads existed
  objectId: string;
  sessionId: string;
  sessionUri: string;
  fileName: string;
  size: number;
  crc32c: string;
  chunkBytes: number;
}
export interface StoredTreeUpload {
  shape: 'tree';
  objectId: string;
  sessionId: string;
  fileCount: number;
  totalBytes: number;
  roots?: RootSummary[]; // absent on records written before roots were kept
}
export type StoredUpload = StoredFileUpload | StoredTreeUpload;

export type Resumable =
  | { shape: 'file'; fileName: string; size: number }
  | {
      shape: 'tree';
      fileCount: number;
      totalBytes: number;
      /** The folders and loose files to pick again; empty when the record predates them. */
      roots: RootSummary[];
      /** What already reached storage and will be skipped; null until known. */
      arrived: { fileCount: number; bytes: number } | null;
    };

export const storageKey = (objectId: string) => 'upload:' + objectId;

function readStored(objectId: string): StoredUpload | null {
  try {
    const raw = localStorage.getItem(storageKey(objectId));
    return raw ? (JSON.parse(raw) as StoredUpload) : null;
  } catch {
    return null;
  }
}

const PROGRESS_REPORT_MS = 5000;

export interface UseUpload {
  state: UploadState;
  /** A previous session for this object exists in this browser; `resume`/`resumeTree` continues it. */
  resumable: Resumable | null;
  start: (file: File) => Promise<void>;
  resume: (file: File) => Promise<void>;
  startTree: (staged: Staged) => Promise<void>;
  resumeTree: (staged: Staged) => Promise<void>;
  cancel: () => void;
  reset: () => void;
}

/** A tree upload's failure as the panel names it: a path the session never declared means the re-picked folders differ. */
function treeError(e: unknown): string {
  if (e instanceof ApiError && e.code === 'upload.path_not_declared') return 'different_tree';
  return e instanceof Error ? e.message : String(e);
}

export function useUpload(objectId: string): UseUpload {
  const qc = useQueryClient();
  const [state, setState] = useState<UploadState>({ phase: 'idle', progress: { done: 0, total: 0 } });
  const [stored, setStored] = useState<StoredUpload | null>(() => readStored(objectId));
  const abort = useRef<AbortController | null>(null);

  useEffect(() => setStored(readStored(objectId)), [objectId]);

  const [arrived, setArrived] = useState<{ fileCount: number; bytes: number } | null>(null);
  const treeSessionId = stored?.shape === 'tree' ? stored.sessionId : null;
  useEffect(() => {
    setArrived(null);
    if (!treeSessionId) return;
    let live = true;
    presentPaths(treeSessionId)
      .then((present) => {
        if (live) setArrived({ fileCount: present.length, bytes: present.reduce((n, p) => n + p.size_bytes, 0) });
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [treeSessionId]);

  const persist = useCallback(
    (s: StoredUpload | null) => {
      if (s) localStorage.setItem(storageKey(objectId), JSON.stringify(s));
      else localStorage.removeItem(storageKey(objectId));
      setStored(s);
    },
    [objectId],
  );

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['object', objectId] });
    void qc.invalidateQueries({ queryKey: ['objects'] });
    void qc.invalidateQueries({ queryKey: ['projects'] });
  }, [qc, objectId]);

  const finish = useCallback(
    async (session: UploadSession, total: number) => {
      setState({ phase: 'verifying', progress: { done: total, total } });
      const result = await completeSession(session.public_id);
      persist(null);
      invalidate();
      if (result.state === 'verified') {
        setState({ phase: 'done', progress: { done: total, total }, result });
      } else {
        setState({ phase: 'failed', progress: { done: total, total }, result, error: result.last_failure ?? 'verify_failed' });
      }
    },
    [invalidate, persist],
  );

  const run = useCallback(
    async (file: File, session: UploadSession, chunkBytes: number) => {
      const ctl = new AbortController();
      abort.current = ctl;
      let lastReport = 0;
      setState({ phase: 'uploading', progress: { done: 0, total: file.size } });
      try {
        await uploadFile({
          sessionUri: session.session_uri,
          file,
          chunkBytes,
          signal: ctl.signal,
          onProgress: (done) => {
            setState({ phase: 'uploading', progress: { done, total: file.size } });
            const now = Date.now();
            if (now - lastReport > PROGRESS_REPORT_MS) {
              lastReport = now;
              void reportProgress(session.public_id, done).catch(() => {});
            }
          },
        });
        await finish(session, file.size);
      } catch (e) {
        if (ctl.signal.aborted) return;
        setState((s) => ({ ...s, phase: 'failed', error: e instanceof Error ? e.message : String(e) }));
      } finally {
        abort.current = null;
      }
    },
    [finish],
  );

  const runTree = useCallback(
    async (files: TreeFile[], session: UploadSession, alreadyBytes: number, total: number) => {
      const ctl = new AbortController();
      abort.current = ctl;
      let lastReport = 0;
      setState({ phase: 'uploading', progress: { done: alreadyBytes, total } });
      try {
        await uploadTree({
          files,
          signal: ctl.signal,
          batchSize: Math.min(200, session.url_batch_max),
          issueUrls: (paths) => issuePutUrls(session.public_id, paths),
          onProgress: (n) => {
            const done = alreadyBytes + n;
            setState({ phase: 'uploading', progress: { done, total } });
            const now = Date.now();
            if (now - lastReport > PROGRESS_REPORT_MS) {
              lastReport = now;
              void reportProgress(session.public_id, done).catch(() => {});
            }
          },
        });
        await finish(session, total);
      } catch (e) {
        if (ctl.signal.aborted) return;
        setState((s) => ({ ...s, phase: 'failed', error: treeError(e) }));
      } finally {
        abort.current = null;
      }
    },
    [finish],
  );

  const startTree = useCallback(
    async (staged: Staged) => {
      const { fileCount, totalBytes } = totals(staged);
      setState({ phase: 'opening', progress: { done: 0, total: totalBytes } });
      try {
        const session = await openSession(objectId, { shape: 'tree', files: declaration(staged) });
        persist({ shape: 'tree', objectId, sessionId: session.public_id, fileCount, totalBytes, roots: summarize(staged) });
        invalidate();
        await runTree(stagedFiles(staged), session, 0, totalBytes);
      } catch (e) {
        setState((s) => ({ ...s, phase: 'failed', error: e instanceof Error ? e.message : String(e) }));
      }
    },
    [objectId, persist, invalidate, runTree],
  );

  const resumeTree = useCallback(
    async (staged: Staged) => {
      const s = readStored(objectId);
      if (!s || s.shape !== 'tree') return startTree(staged);
      const { fileCount, totalBytes } = totals(staged);
      const fail = (error: string, mismatch?: RootDiff) =>
        setState({ phase: 'failed', progress: { done: 0, total: totalBytes }, error, mismatch });
      if (s.roots) {
        const diff = compareRoots(s.roots, staged);
        if (!sameRoots(diff)) return fail('different_tree', diff);
      }
      if (fileCount !== s.fileCount || totalBytes !== s.totalBytes) return fail('different_tree');
      setState({ phase: 'opening', progress: { done: 0, total: totalBytes } });
      try {
        const session = await getSession(s.sessionId);
        if (session.state !== 'open') {
          persist(null);
          return fail('session_' + session.state);
        }
        const present = await presentPaths(s.sessionId);
        const sizes = new Map(stagedFiles(staged).map((f) => [f.path, f.size]));
        if (present.some((p) => sizes.get(p.path) !== p.size_bytes)) return fail('different_tree');
        const skip = new Set(present.map((p) => p.path));
        const already = present.reduce((n, p) => n + p.size_bytes, 0);
        await runTree(stagedFiles(staged).filter((f) => !skip.has(f.path)), session, already, totalBytes);
      } catch (e) {
        setState((st) => ({ ...st, phase: 'failed', error: treeError(e) }));
      }
    },
    [objectId, persist, runTree, startTree],
  );

  const start = useCallback(
    async (file: File) => {
      setState({ phase: 'hashing', progress: { done: 0, total: file.size } });
      try {
        const crc = await crc32cOfFile(file, (done) => setState({ phase: 'hashing', progress: { done, total: file.size } }));
        setState({ phase: 'opening', progress: { done: 0, total: file.size } });
        const session = await openSession(objectId, {
          shape: 'file',
          name: file.name,
          size_bytes: file.size,
          crc32c: crc,
          content_type: file.type || 'application/octet-stream',
        });
        persist({
          objectId,
          sessionId: session.public_id,
          sessionUri: session.session_uri,
          fileName: file.name,
          size: file.size,
          crc32c: crc,
          chunkBytes: session.chunk_bytes,
        });
        invalidate();
        await run(file, session, session.chunk_bytes);
      } catch (e) {
        setState((s) => ({ ...s, phase: 'failed', error: e instanceof Error ? e.message : String(e) }));
      }
    },
    [objectId, persist, invalidate, run],
  );

  const resume = useCallback(
    async (file: File) => {
      const s = readStored(objectId);
      if (!s) return start(file);
      if (s.shape === 'tree') return start(file);
      if (file.name !== s.fileName || file.size !== s.size) {
        setState({ phase: 'failed', progress: { done: 0, total: file.size }, error: 'different_file' });
        return;
      }
      setState({ phase: 'hashing', progress: { done: 0, total: file.size } });
      try {
        const crc = await crc32cOfFile(file, (done) => setState({ phase: 'hashing', progress: { done, total: file.size } }));
        if (crc !== s.crc32c) {
          setState({ phase: 'failed', progress: { done: 0, total: file.size }, error: 'different_file' });
          return;
        }
        const session = await getSession(s.sessionId);
        if (session.state !== 'open') {
          persist(null);
          setState({ phase: 'failed', progress: { done: 0, total: file.size }, error: 'session_' + session.state });
          return;
        }
        await run(file, session, s.chunkBytes);
      } catch (e) {
        setState((st) => ({ ...st, phase: 'failed', error: e instanceof Error ? e.message : String(e) }));
      }
    },
    [objectId, persist, run, start],
  );

  const cancel = useCallback(() => {
    abort.current?.abort();
    const s = readStored(objectId);
    if (s) void abandonSession(s.sessionId).catch(() => {});
    persist(null);
    invalidate();
    setState({ phase: 'idle', progress: { done: 0, total: 0 } });
  }, [objectId, persist, invalidate]);

  const reset = useCallback(() => setState({ phase: 'idle', progress: { done: 0, total: 0 } }), []);

  return {
    state,
    resumable: !stored
      ? null
      : stored.shape === 'tree'
        ? { shape: 'tree', fileCount: stored.fileCount, totalBytes: stored.totalBytes, roots: stored.roots ?? [], arrived }
        : { shape: 'file', fileName: stored.fileName, size: stored.size },
    start,
    resume,
    startTree,
    resumeTree,
    cancel,
    reset,
  };
}
